import { SecuredLayout } from '../Layout/SecuredLayout';
import { Button, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import Navbar from '../Layout/Navbar';
import React, { useCallback, useEffect, useState } from 'react';
import { usePartnerList } from '../../hooks/usePartnerList';
import { useAuth, useProvideSnackBar } from '../../hooks';
import { DefaultPartner, LineName, Partner } from '../../@types/workplace';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import { Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Role } from '../../@types/auth';
import { usePreconfiguredFetch } from '../../hooks/useProvidePreconfiguredFetch';
import ConfirmDeleteLineNameModal from './ConfirmDeleteLineNameModal';
import AddLineNameModal from './AddLineNameModal';
import { StyledGrid } from '../Layout/styles';

export default function LineNames() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { partnerList } = usePartnerList();
    const { showError, showSuccess, showResponseError } = useProvideSnackBar();
    const { fetchResource } = usePreconfiguredFetch();

    const [partner, setPartner] = useState<Partner>(DefaultPartner)
    const [lineNames, setLineNames] = useState<Array<LineName>>()
    const [addModalOpen, setAddModalOpen] = useState<boolean>(false)
    const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState<boolean>(false)
    const [lineNameIdToDelete, setLineNameIdToDelete] = useState<number>(0)

    useEffect(() => {
        if (!(user?.hasRole(Role.FOVEZENYLO) || user?.hasRole(Role.VEZENYLO) || user?.hasRole(Role.ADMIN))) {
            navigate('/');
        }
    }, [user, navigate]);

    const fetchLineNames = useCallback(async () => {
        if (partner.ID === 0) {
            return;
        }

        try {
            const response = await fetchResource(`/api/partners/${partner.ID}/line_names`);
            if (!response.ok) {
                showResponseError(response);
                return;
            }
            setLineNames(await response.json())
        } catch (error: any) {
            showError('Hiba történt: ' + error.message);
        }
    }, [user, partner])

    useEffect(() => {
        fetchLineNames();
    }, [partner]);

    const selectPartnerHandler = (selectedName: string) => {
        const selectedPartner: Partner = partnerList.filter(p => p.Name === selectedName)[0] || DefaultPartner;

        setPartner(selectedPartner);
    }

    const deleteLineNameHandler = async () => {
        if (partner.ID === 0 || lineNameIdToDelete === 0) {
            showError("Sikertelen törlés")
            return;
        }

        try {
            const response = await fetchResource(`/api/partners/${partner.ID}/line_name/${lineNameIdToDelete}`, {
                method: 'DELETE',
            })
            if (!response.ok) {
                showResponseError(response);
                return;
            }
            showSuccess('Sikeres törlés');
            fetchLineNames();
        } catch (error: any) {
            showError('Hiba történt: ' + error.message);
        } finally {
            onCloseConfirmModal();
        }
    }

    const onDeleteLineName = (id: number) => {
        setLineNameIdToDelete(id);
        setConfirmDeleteModalOpen(true);
    }

    const onCloseConfirmModal = () => {
        setConfirmDeleteModalOpen(false)
        setLineNameIdToDelete(0)
    }

    return (
        <SecuredLayout>
            <Grid container px={1} py={1} spacing={1}>
                <Grid item xs={12} px={1.5} py={0.5} textAlign={'right'}>
                    <Navbar/>
                </Grid>
            </Grid>
            <Grid container display='flex' alignItems='center' justifyContent='flex-start' spacing={2} px={2} py={0.5}>
                <StyledGrid item width={'100%'} marginLeft={'0.55rem'} paddingY={'1rem'}>
                    <FormControl margin='normal'>
                        <InputLabel id='workplace-label' variant='standard'>
                            Partner
                        </InputLabel>
                        <Select
                            variant='standard'
                            labelId='workplace-label'
                            value={partner.Name}
                            label='Partner'
                            sx={{ minWidth: '200px' }}
                            onChange={e => selectPartnerHandler(e.target.value as string)}
                        >
                            <MenuItem value={'please_select'}>Kérem válasszon!</MenuItem>
                            {!!partnerList && partnerList.map(workplace => {
                                return (
                                    <MenuItem key={workplace.ID} value={workplace.Name}>
                                        {workplace.Name}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                    {partner.ID !== 0 && (
                        <>
                            {!!lineNames && lineNames.map(lineName => {
                                return (
                                    <Grid key={lineName.ID} container display='flex' alignItems='center'
                                          marginY='0.5rem'>
                                        <Typography>{lineName.Name}</Typography>
                                        {(user?.hasRole(Role.FOVEZENYLO) || user?.hasRole(Role.ADMIN)) && (
                                            <>
                                                <Typography variant='caption' color='textSecondary'
                                                            style={{ display: 'flex', alignItems: 'end' }}>
                                                    <Tooltip title='Törlés'>
                                                        <DeleteIcon fontSize='small' cursor='pointer'
                                                                    style={{ marginLeft: '5px', marginRight: '5px' }}
                                                                    onClick={() => onDeleteLineName(lineName.ID)}/>
                                                    </Tooltip>
                                                </Typography>
                                                <ConfirmDeleteLineNameModal open={confirmDeleteModalOpen}
                                                                            onClose={onCloseConfirmModal}
                                                                            onDelete={deleteLineNameHandler}/>
                                            </>
                                        )}
                                    </Grid>
                                )
                            })}
                            {(user?.hasRole(Role.FOVEZENYLO) || user?.hasRole(Role.ADMIN)) && (
                                <>
                                    <Grid>
                                        <FormControl margin='normal'>
                                            <Button variant='contained' startIcon={<Add/>} sx={{ borderRadius: '50px' }}
                                                    onClick={() => setAddModalOpen(true)}>
                                                Járatnév hozzáadása
                                            </Button>
                                        </FormControl>
                                    </Grid>
                                    <AddLineNameModal open={addModalOpen} onClose={() => setAddModalOpen(false)}
                                                      onDataChange={fetchLineNames} partnerId={partner.ID}/>
                                </>
                            )}
                        </>
                    )
                    }
                </StyledGrid>
            </Grid>
        </SecuredLayout>
    )
};